import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import {
  BodyText,
  ListActionPressable,
  usePressableState,
  useResponsive,
} from '@sg/garnish';
import type { DeliveryPreferenceType } from '@sg/graphql-schema';

import { usePreferencesText } from './hooks';

export const DeliveryPreferencesItem = ({
  preferences,
  onPress,
}: DeliveryPreferencesItemProps) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  const preferencesText = usePreferencesText(preferences.type);

  const pressableRef = useRef(null);
  const { isActive, isFocused, isHovered } = usePressableState(pressableRef);
  const active = isActive || isFocused || isHovered;

  return (
    <ListActionPressable
      testID="delivery.preferences.trigger"
      ref={pressableRef}
      withCaret={currentBreakpoint.isXS}
      accessibilityRole="button"
      accessibilityLabel={formatMessage(messages.deliveryPreferencesTitle)}
      accessibilityHint={formatMessage(messages.deliveryPreferencesTitle)}
      onPress={onPress}
      iconName="IconBagStroke"
    >
      <BodyText size={4} bold style={[styles.title, active && styles.active]}>
        {preferencesText}
      </BodyText>
      <BodyText size={4} lineBreakMode="head">
        {}
        {preferences.notes ||
          formatMessage(messages.deliveryPreferencesNoteHint)}
      </BodyText>
    </ListActionPressable>
  );
};

type DeliveryPreferences = Readonly<{
  type: DeliveryPreferenceType;
  notes?: string;
}>;

type DeliveryPreferencesItemProps = Readonly<{
  preferences: DeliveryPreferences;
  onPress: () => void;
}>;

const styles = StyleSheet.create({
  title: {
    textTransform: 'capitalize',
  },
  active: {
    textDecorationLine: 'underline',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryPreferencesTitle: {
    defaultMessage: 'Edit delivery details',
    description: 'Delivery | Preferences | Title',
  },
  deliveryPreferencesNoteHint: {
    defaultMessage: 'Add a note for the courier',
    description: 'Delivery | Preferences | Note Hint',
  },
});
